import React, { useState } from "react";
import { Box, Typography, Chip, Avatar, Grid } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import {
  Payment as PaymentIcon,
  DirectionsCar,
  TwoWheeler,
  Phone,
  CheckCircle,
  Cancel,
  AttachMoney,
} from "@mui/icons-material";
import { StatCard } from "../../components/common";
import { calculatePaymentStats } from "../../utils/calculations";
import HeroSection from "../../components/HeroSection";
import { useGetPaymentDetailsQuery } from "../../features/messages/messagesApiSlice";
import { useGetDailyAnalyticsQuery } from "../../features/analytics/analyticsApiSlice";
import { format, isValid } from "date-fns";
import { PaymentDetail } from "../../types";
import { TableColumn } from "../../components/DataTable";
import DataTableCard from "../../components/DataTableCard";
import { useAnalyticsDateParams } from "../../utils/dateFilterUtils";
import { useAppSelector } from "../../store/store";
import PageToolbar from "../../components/Toolbar/PageToolbar";
import { useCacheValidation, useErrorToast } from "../../hooks";
import ScrollToTopFab from "../../components/ScrollToTopFab";

const Payments: React.FC = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);

  // Use scroll-to-top hook for button visibility and scroll functionality

  // Get date parameters for API calls
  const dateParams = useAnalyticsDateParams();

  // venueId is extracted from JWT token by backend automatically

  // Check if we have cached data from Dashboard using custom hook
  const cachedDailyData = useAppSelector((state) => state.analytics.dailyCache);
  const isCacheValid = useCacheValidation({
    cache: cachedDailyData,
    startDate: dateParams.startDate,
    endDate: dateParams.endDate,
    cacheTTL: 60000, // 1 minute cache
  });

  // Get payment summary metrics from analytics API (for summary cards)
  // Skip API call if we have valid cached data from Dashboard
  const {
    data: analyticsData,
    isLoading: isLoadingAnalytics,
    isFetching: isFetchingAnalytics,
    isError: isAnalyticsError,
  } = useGetDailyAnalyticsQuery(
    {
      ...dateParams,
    },
    { skip: isCacheValid },
  );

  // Get payment details from payment API (for table)
  const {
    data: paymentResponse,
    isLoading: isLoadingPayments,
    isFetching: isFetchingPayments,
    isError: isPaymentError,
  } = useGetPaymentDetailsQuery({
    startDate: dateParams.startDate,
    endDate: dateParams.endDate,
    page,
    limit,
  });

  // Get current error state
  const isError = (!isCacheValid && isAnalyticsError) || isPaymentError;
  const isFetching = isFetchingAnalytics || isFetchingPayments;
  const isLoading = (isLoadingAnalytics && !isCacheValid) || isLoadingPayments;

  // Use cached data if available, otherwise use fresh data
  const dailyData =
    isCacheValid && cachedDailyData.data
      ? cachedDailyData.data[0]
      : analyticsData?.data?.[0];

  // Extract payment metrics from analytics data
  const totalRevenue = dailyData?.totalRevenue || 0;
  const totalTickets = dailyData?.totalTickets || 0;
  const avgTicketValue = totalTickets > 0 ? totalRevenue / totalTickets : 0;

  // Handle vehicleBreakdown which can be either number or object with count property
  const carBreakdown = dailyData?.vehicleBreakdown?.Car;
  const bikeBreakdown = dailyData?.vehicleBreakdown?.Bike;
  const carCount =
    typeof carBreakdown === "object" && carBreakdown !== null
      ? carBreakdown.count
      : carBreakdown || 0;
  const bikeCount =
    typeof bikeBreakdown === "object" && bikeBreakdown !== null
      ? bikeBreakdown.count
      : bikeBreakdown || 0;

  // Payment details from table API
  const payments: PaymentDetail[] = paymentResponse?.data || [];
  const pagination = paymentResponse?.pagination;

  // Calculate payment statistics using utility function
  const paymentStats = calculatePaymentStats(payments);

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case "success":
      case "captured":
        return <CheckCircle fontSize="small" />;
      case "failed":
        return <Cancel fontSize="small" />;
      default:
        return <CheckCircle fontSize="small" />;
    }
  };

  // Handle error toast notifications with automatic deduplication and mount delay
  useErrorToast({
    isError,
    isFetching,
    isLoading,
    errorMessage: "Failed to load payment data. Please try again.",
  });

  // Table columns definition
  const tableColumns: TableColumn<PaymentDetail>[] = [
    {
      key: "customer",
      label: "Customer & Vehicle",
      render: (_, payment) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Avatar
            sx={{
              width: 32,
              height: 32,
              bgcolor: payment.vehicleType === "Car" ? "#1a237e" : "#4facfe",
            }}
          >
            {payment.vehicleType === "Car" ? (
              <DirectionsCar fontSize="small" />
            ) : (
              <TwoWheeler fontSize="small" />
            )}
          </Avatar>
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Phone fontSize="small" color="action" />
              <Typography variant="body2" fontWeight={500}>
                {payment.customerMobile}
              </Typography>
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontFamily: "monospace" }}
            >
              {payment.ticketId}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      key: "amount",
      label: "Amount",
      render: (_, payment) => (
        <Box>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{
              color:
                payment.status.toLowerCase() === "success"
                  ? "#10b981"
                  : "text.primary",
            }}
          >
            ₹{payment.amount.toFixed(2)}
          </Typography>
        </Box>
      ),
    },
    {
      key: "status",
      label: "Payment Status",
      render: (_, payment) => {
        const status = payment.status.toLowerCase();
        return (
          <Chip
            label={payment.status}
            size="small"
            icon={getStatusIcon(payment.status)}
            sx={{
              textTransform: "capitalize",
              bgcolor:
                status === "success" || status === "captured"
                  ? "#10b981"
                  : status === "pending"
                    ? "#f59e0b"
                    : status === "failed"
                      ? "#ef4444"
                      : "#6b7280",
              color: "white",
              fontWeight: 600,
              "& .MuiChip-icon": {
                color: "white",
              },
            }}
          />
        );
      },
    },
    {
      key: "transactionDate",
      label: "Transaction Date",
      render: (_, payment) => (
        <Box>
          <Typography variant="body2" color="text.secondary">
            {(() => {
              try {
                const txnDate = new Date(payment.transactionDate);
                return isValid(txnDate)
                  ? format(txnDate, "MMM dd, yyyy HH:mm")
                  : "Invalid Date";
              } catch (error) {
                return "Invalid Date";
              }
            })()}
          </Typography>
        </Box>
      ),
    },
    {
      key: "transactionId",
      label: "Transaction ID",
      render: (_, payment) => (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontFamily: "monospace" }}
        >
          {payment.transactionId
            ? payment.transactionId.substring(0, 16) + "..."
            : "N/A"}
        </Typography>
      ),
    },
    {
      key: "paymentId",
      label: "Payment ID",
      render: (_, payment) => (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontFamily: "monospace" }}
        >
          {payment.paymentId
            ? payment.paymentId.substring(0, 16) + "..."
            : "N/A"}
        </Typography>
      ),
    },
  ];

  // isLoading, isError, and isFetching already defined above

  return (
    <>
      <PageToolbar showDateFilter />

      {/* Hero Section with Framer Motion */}
      <HeroSection
        title="Payment Transactions"
        subtitle="Parking fee collections, payment processing status, and revenue analytics"
        gradient="navyLux"
      />

      <Box sx={{ py: { xs: 3, sm: 4 }, px: { xs: 2, sm: 2, md: 1.5 } }}>
        {/* Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key="payments-content"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <Box sx={{ position: "relative" }}>
              {/* Section Header: Revenue Metrics */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 48,
                      height: 48,
                      borderRadius: 2.5,
                      background:
                        "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
                      boxShadow: "0 6px 16px rgba(250, 112, 154, 0.3)",
                    }}
                  >
                    <AttachMoney sx={{ fontSize: 24, color: "white" }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 800,
                        color: "text.primary",
                        letterSpacing: "-0.5px",
                        fontSize: { xs: "1.2rem", md: "1.4rem" },
                        mb: 0.5,
                      }}
                    >
                      Revenue Analytics
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        fontSize: "0.85rem",
                        fontWeight: 500,
                      }}
                    >
                      Parking revenue performance and payment transaction
                      metrics
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Summary Cards - Using Analytics Data */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    style={{ height: "100%" }}
                  >
                    <StatCard
                      label="Total Revenue"
                      value={`₹${totalRevenue.toFixed(2)}`}
                      subtitle="All parking fees collected"
                      icon={<AttachMoney />}
                      gradient="royalBlue"
                      isLoading={isLoading || isFetchingAnalytics}
                    />
                  </motion.div>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    style={{ height: "100%" }}
                  >
                    <StatCard
                      label="Avg Ticket Value"
                      value={`₹${avgTicketValue.toFixed(2)}`}
                      subtitle="Average per transaction"
                      icon={<AttachMoney />}
                      gradient="gold"
                      isLoading={isLoading || isFetchingAnalytics}
                    />
                  </motion.div>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                    style={{ height: "100%" }}
                  >
                    <StatCard
                      label="Successful Payments"
                      value={paymentStats.successful}
                      subtitle="Completed transactions"
                      icon={<CheckCircle />}
                      gradient="emerald"
                      isLoading={isLoading || isFetchingPayments}
                    />
                  </motion.div>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                    style={{ height: "100%" }}
                  >
                    <StatCard
                      label="Cars / Bikes"
                      value={`${carCount} / ${bikeCount}`}
                      subtitle="Vehicle breakdown"
                      icon={<DirectionsCar />}
                      gradient="deepPurple"
                      isLoading={isLoading || isFetchingAnalytics}
                    />
                  </motion.div>
                </Grid>
              </Grid>

              {/* Payment Transactions Table */}
              <DataTableCard
                title={`Payment Transactions${
                  pagination ? ` (${pagination.total})` : ""
                }`}
                data={payments}
                columns={tableColumns}
                headerBackground="grey.50"
                emptyState={{
                  icon: <PaymentIcon sx={{ fontSize: 48, opacity: 0.5 }} />,
                  title: "No payment transactions found",
                  subtitle:
                    "Payment transactions will appear here when customers make parking payments",
                }}
                isLoading={isLoadingPayments}
                isFetching={isFetchingPayments}
                enableSearch
                searchPlaceholder="Search by mobile, transaction ID, payment ID..."
                searchableColumns={[
                  "customerMobile",
                  "transactionId",
                  "paymentId",
                  "ticketId",
                ]}
                showTableOnEmpty
                pagination={
                  pagination
                    ? {
                        page,
                        limit,
                        total: pagination.total,
                        onPageChange: setPage,
                        onLimitChange: setLimit,
                      }
                    : undefined
                }
                skeletonConfig={{
                  rows: 6,
                  showAvatar: true,
                  textColumns: 3,
                  chipColumns: 1,
                  headerWidth: 280,
                }}
              />
            </Box>
          </motion.div>
        </AnimatePresence>
      </Box>

      {/* Scroll to top button */}
      <ScrollToTopFab />
    </>
  );
};

/**
 * Memoized Payments component for optimal performance.
 * Prevents unnecessary re-renders when parent components update.
 */
export default React.memo(Payments);
